import { reportingLogger } from 'helpers/reporting-logger';

const DIAGNOSTICS_SESSION_KEY = 'reporting-uat-session-correlation-id';
const DIAGNOSTICS_QUERY_PARAM = 'diag';
const MAX_TELEMETRY_VALUE_LENGTH = 1024;
const MAX_DIAGNOSTIC_HISTORY = 200;

let diagnosticSequence = 0;
let diagnosticHistory: UatDiagnosticEnvelope[] = [];

type AppInsightsLike = {
  trackEvent?: (event: { name: string }, properties?: Record<string, string>) => void;
  trackTrace?: (trace: { message: string; severityLevel?: number }, properties?: Record<string, string>) => void;
};

type UatDiagnosticPayload = Record<string, unknown>;
export type UatDiagnosticEnvelope = {
  eventName: string;
  diagnosticSequence: number;
  timestampUtc: string;
  sessionCorrelationId: string;
} & UatDiagnosticPayload;

declare global {
  interface Window {
    appInsights?: AppInsightsLike;
  }
}

export type UatDiagnosticsContext = {
  enabled: boolean;
  sessionCorrelationId: string;
};

const isBrowser = (): boolean => typeof window !== 'undefined';

const isTruthyQueryFlag = (value: string | null): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
};

const isUatHostname = (hostname: string): boolean => {
  const normalizedHost = hostname.toLowerCase();
  return normalizedHost.includes('reporting-uat')
    || normalizedHost.includes('reporting-uat2')
    || normalizedHost.includes('uat2.personicom.com');
};

const shouldEnableDiagnostics = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  const query = new URLSearchParams(window.location.search);
  if (isTruthyQueryFlag(query.get(DIAGNOSTICS_QUERY_PARAM))) {
    return true;
  }

  return isUatHostname(window.location.hostname);
};

const createCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const timestamp = Date.now().toString(36);
  const randomSegment = Math.random().toString(36).slice(2, 10);
  return `${timestamp}-${randomSegment}`;
};

const getOrCreateSessionCorrelationId = (): string => {
  if (!isBrowser()) {
    return 'server-render';
  }

  try {
    const existing = window.sessionStorage.getItem(DIAGNOSTICS_SESSION_KEY);
    if (existing) {
      return existing;
    }

    const generated = createCorrelationId();
    window.sessionStorage.setItem(DIAGNOSTICS_SESSION_KEY, generated);
    return generated;
  } catch {
    // Some browsers/contexts block storage; fall back to in-memory generated id.
    return createCorrelationId();
  }
};

const normalizeTelemetryValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.slice(0, MAX_TELEMETRY_VALUE_LENGTH);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(normalizeTelemetryValue).join(',').slice(0, MAX_TELEMETRY_VALUE_LENGTH);
  }

  try {
    return JSON.stringify(value).slice(0, MAX_TELEMETRY_VALUE_LENGTH);
  } catch {
    return String(value).slice(0, MAX_TELEMETRY_VALUE_LENGTH);
  }
};

const buildTelemetryProperties = (payload: UatDiagnosticPayload): Record<string, string> => {
  return Object.entries(payload).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = normalizeTelemetryValue(value);
    return acc;
  }, {});
};

const emitConsoleDiagnostic = (eventName: string, envelope: UatDiagnosticPayload): void => {
  if (!isBrowser()) {
    return;
  }

  // Emit directly to console so diagnostics remain visible even when logger level is NONE on UAT hosts.
  console.info(`[UAT Diagnostics] ${eventName}`, envelope);
};

export const getUatDiagnosticsContext = (): UatDiagnosticsContext => {
  const enabled = shouldEnableDiagnostics();
  return {
    enabled,
    sessionCorrelationId: getOrCreateSessionCorrelationId(),
  };
};

export const reportUatDiagnostic = (
  eventName: string,
  payload: UatDiagnosticPayload = {}
): void => {
  const context = getUatDiagnosticsContext();
  if (!context.enabled) {
    return;
  }

  diagnosticSequence += 1;

  const envelope: UatDiagnosticEnvelope = {
    eventName,
    diagnosticSequence,
    timestampUtc: new Date().toISOString(),
    sessionCorrelationId: context.sessionCorrelationId,
    ...payload,
  };

  diagnosticHistory = [...diagnosticHistory, envelope].slice(-MAX_DIAGNOSTIC_HISTORY);

  emitConsoleDiagnostic(eventName, envelope);
  reportingLogger.info(`[UAT Diagnostics] ${eventName}`, envelope, 'Reporting');

  if (!isBrowser() || !window.appInsights?.trackEvent) {
    return;
  }

  try {
    window.appInsights.trackEvent(
      { name: `ReportingUAT.${eventName}` },
      buildTelemetryProperties(envelope),
    );
  } catch (error) {
    reportingLogger.warn('Failed to push UAT diagnostic event to App Insights', error, 'Reporting');
  }
};

export const getUatDiagnosticHistory = (): UatDiagnosticEnvelope[] => [...diagnosticHistory];

export const clearUatDiagnosticHistory = (): void => {
  diagnosticHistory = [];
  diagnosticSequence = 0;
};
